import {
  NotificationHookFragment,
  NotificationsMenuNotificationFragment,
} from '@/generated/graphql';
import {captureMessage} from '@sentry/react';

type RoutableNotification =
  | NotificationsMenuNotificationFragment
  | NotificationHookFragment;

const logUnroutedNotification = (notification: RoutableNotification) => {
  const targetType = notification.target?.__typename ?? 'null';
  const actionType = notification.actionObject?.__typename ?? 'null';
  captureMessage(
    `getNotificationRoute fell through — id=${notification.id} target=${targetType} actionObject=${actionType}`,
    'warning',
  );
};

export type NotificationNavigationMetaDescriptor = {
  targetType: string | null;
  actionObjectType: string | null;
  routeId: string | null;
  secondaryId: string | null;
};

const logUnroutedDescriptor = (
  descriptor: NotificationNavigationMetaDescriptor,
) => {
  captureMessage(
    `getLeanNotificationRoute fell through — target=${descriptor.targetType ?? 'null'} actionObject=${descriptor.actionObjectType ?? 'null'}`,
    'warning',
  );
};

export const getLeanNotificationRoute = (
  descriptor: NotificationNavigationMetaDescriptor,
): string | null => {
  const {targetType, actionObjectType, routeId, secondaryId} = descriptor;

  switch (actionObjectType) {
    case 'residential_management.application':
      if (routeId) return `/verification/${routeId}`;
      logUnroutedDescriptor(descriptor);
      return null;
    case 'residential_management.communityupdatecomment':
      if (targetType === 'residential_management.communityupdate' && routeId) {
        return `/updates/${routeId}/comments`;
      }
      logUnroutedDescriptor(descriptor);
      return null;
    case 'residential_management.keyholder':
      if (targetType === 'residential_management.key' && routeId) {
        return `/keys/${routeId}`;
      }
      logUnroutedDescriptor(descriptor);
      return null;
    case 'residential_management.communityproductpurchase':
      if (targetType === 'residential_management.communityproduct' && routeId) {
        return `/community-products/${routeId}`;
      }
      logUnroutedDescriptor(descriptor);
      return null;
    case 'residential_management.amenityreservation':
      if (targetType === 'residential_management.amenity' && routeId) {
        return `/facilities/${routeId}`;
      }
      logUnroutedDescriptor(descriptor);
      return null;
    default:
      break;
  }

  switch (targetType) {
    case 'residential_management.application':
      if (routeId) return `/verification/${routeId}`;
      break;
    case 'residential_management.conversation':
      if (routeId) return `/messages/${routeId}`;
      break;
    case 'residential_management.communityupdate':
      if (routeId) return `/updates/${routeId}/edit`;
      break;
    case 'residential_management.residentsurvey':
      if (routeId) return `/surveys/poll/${routeId}`;
      break;
    case 'residential_management.key':
      if (routeId) return `/keys/${routeId}`;
      break;
    case 'tenancy_application.tenantreference':
      if (routeId) return `/tenant-reference-report/${routeId}`;
      break;
    case 'residential_management.communityevent':
      if (routeId) return `/events/${routeId}`;
      break;
    case 'residential_management.conversationassignment':
      if (routeId) return `/messages/${routeId}`;
      break;
    case 'residential_management.teamassignment':
      if (routeId) return `/messages/${routeId}`;
      break;
    case 'residential_management.featureannouncement':
      if (routeId) return `/features/${routeId}`;
      break;
    case 'tasks.task':
      if (routeId) return `/tasks/${routeId}`;
      break;
    case 'residential_management.applicationthread':
      if (routeId) return `/verification/${routeId}`;
      break;
    case 'residential_management.marketplaceitemthread':
      if (secondaryId) return `/marketplace/${secondaryId}`;
      break;
    case 'residential_management.documentextraction':
      if (routeId) return `/safety-center/inspections/${routeId}`;
      return '/safety-center/inspections';
    default:
      break;
  }

  logUnroutedDescriptor(descriptor);
  return null;
};

export const getNotificationRoute = (
  notification: RoutableNotification,
): string | null => {
  if (notification.actionObject) {
    switch (notification.actionObject.__typename) {
      case 'ApplicationNode':
        return `/verification/${notification.actionObject.id}`;
      case 'CommunityUpdateCommentNode':
        if (notification.target?.__typename === 'CommunityUpdateNode') {
          return `/updates/${notification.target.id}/comments`;
        }
        logUnroutedNotification(notification);
        return null;
      case 'KeyholderNode':
        if (notification.target?.__typename === 'KeyNode') {
          return `/keys/${notification.target.id}`;
        }
        logUnroutedNotification(notification);
        return null;
      case 'CommunityProductPurchaseNode':
        if (notification.target?.__typename === 'CommunityProductNode') {
          return `/community-products/${notification.target.id}`;
        }
        logUnroutedNotification(notification);
        return null;
      case 'AmenityReservationNode':
        if (notification.target?.__typename === 'AmenityNode') {
          return `/facilities/${notification.target.id}`;
        }
        logUnroutedNotification(notification);
        return null;
      default:
        break;
    }
  }

  switch (notification.target?.__typename) {
    case 'ApplicationNode':
      return `/verification/${notification.target.id}`;
    case 'ConversationNode':
      return `/messages/${notification.target.id}`;
    case 'CommunityUpdateNode':
      return `/updates/${notification.target.id}/edit`;
    case 'ResidentSurveyNode':
      return `/surveys/poll/${notification.target.id}`;
    case 'KeyNode':
      return `/keys/${notification.target.id}`;
    case 'TenantReferenceNode':
      return `/tenant-reference-report/${notification.target.uuid}`;
    case 'CommunityEventNode':
      return `/events/${notification.target.id}`;
    case 'ConversationAssignmentNode':
      return `/messages/${notification.target.conversation.id}`;
    case 'TeamAssignmentNode':
      return `/messages/${notification.target.objectId}`;
    case 'FeatureAnnouncementNode':
      return `/features/${notification.target.slug}`;
    case 'TaskNode':
      return `/tasks/${notification.target.id}`;
    case 'ApplicationThreadNode': {
      const applicationId =
        notification.target.applicationId ??
        notification.target.application?.id;
      if (!applicationId) {
        logUnroutedNotification(notification);
        return null;
      }
      return `/verification/${applicationId}`;
    }
    case 'DocumentExtractionNode': {
      if (notification.target.extractedTaskId) {
        return `/safety-center/inspections/${notification.target.extractedTaskId}`;
      }
      return '/safety-center/inspections';
    }
    default:
      logUnroutedNotification(notification);
      return null;
  }
};
